/**
 * The id used for the payment gateway iframe
 */
export const PAYMENT_GATEWAY_IFRAME = 'vd-payment-gateway'

/**
 * Constants related to the payment steps used via the window.postMessage api interface between the payment portal and
 * webregister.
 */
export enum PAYMENT_STEPS {
  /**
   * Request additional information from Vend about the sale and payment.
   */
  DATA = 'DATA',

  /**
   * Customize the payment dialog.
   */
  SETUP = 'SETUP',

  /**
   * Prints a receipt. You can add content to the receipt with the receipt_html_extra field and if the receipt prints a
   * merchant or customer copy with the copy_text field as described below.
   */
  PRINT = 'PRINT',

  /**
   * Trigger a successful transaction. If the payment type supports printing (and it’s enabled) an approved transaction
   * receipt will also print.
   */
  ACCEPT = 'ACCEPT',

  /**
   * Cancel the transaction. If the payment type supports printing (and it’s enabled) a declined transaction receipt
   * will also print.
   */
  DECLINE = 'DECLINE',

  /**
   * Cleanly exit the process. Doesn’t close the window but closes all other dialogs and unbinds postMessage handling.
   */
  EXIT = 'EXIT',

  /**
   * Payment modal is dismissed.
   */
  DISMISSED = 'DISMISSED MODAL',

  /**
   * Prints a signature slip receipt. You can add the signature to the receipt with the signature_base64 or signature_url
   * field and prints a card last four digital number with card_last_four,
   * prints the payment time with payment_date and
   * prints terminal id with terminal_serial_number as described below.
   */
  ACCEPT_SIGNATURE = 'ACCEPT_SIGNATURE',
}

// Steps that are final, whatever the outcome is
export const FINAL_STEPS = [
  PAYMENT_STEPS.ACCEPT_SIGNATURE,
  PAYMENT_STEPS.ACCEPT,
  PAYMENT_STEPS.DECLINE,
  PAYMENT_STEPS.EXIT,
  PAYMENT_STEPS.DISMISSED,
]

export interface PaymentStepSetup {
  step: PAYMENT_STEPS.SETUP
  setup: {
    enable_close?: boolean
    heading?: string
    display_gateway_fullscreen?: boolean
  }
}

export interface PaymentStepData {
  step: PAYMENT_STEPS.DATA
}

export interface PaymentStepPrint {
  step: PAYMENT_STEPS.PRINT
  receipt_html_extra?: string
}

export interface PaymentStepAccept {
  step: PAYMENT_STEPS.ACCEPT
}

export interface PaymentStepDecline {
  step: PAYMENT_STEPS.DECLINE
}

export interface PaymentStepExit {
  step: PAYMENT_STEPS.EXIT
}

export interface PaymentStepDismissed {
  step: PAYMENT_STEPS.DISMISSED
}

export interface PaymentStepPrintSignatureSlip {
  step: PAYMENT_STEPS.ACCEPT_SIGNATURE
  card_last_four?: string
  external_transaction_id?: string
  payment_date?: string
  terminal_serial_number?: string
  signature_url?: string
  signature_base64?: string
  transaction_status?: string
}

export type PaymentStepEvent =
  | PaymentStepSetup
  | PaymentStepData
  | PaymentStepPrint
  | PaymentStepAccept
  | PaymentStepDecline
  | PaymentStepExit
  | PaymentStepPrintSignatureSlip

export interface PaymentResponseData {
  payment: {
    amount: string
    external_transaction_id?: string[]
    register_sale_payment_id?: string[]
    reference_id?: string
    register_id: string
  }
  register_sale: any
  step: PAYMENT_STEPS
  success: boolean
}

export type AcceptStep = PaymentStepAccept | PaymentStepPrintSignatureSlip

export type RejectStep =
  | PaymentStepDecline
  | PaymentStepExit
  | PaymentStepDismissed
